import React, { useState } from 'react';
import '@/App.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Phone, Clock, Menu, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  fi: {
    nav: {
      home: 'Etusivu',
      story: 'Tarinamme',
      location: 'Sijainti',
      orderOnline: 'Tilaa Verkossa'
    },
    hero: {
      welcome: 'Tervetuloa',
      title: 'Pizza Sofra',
      subtitle: '',
      orderButton: 'Katso menu ja tilaa'
    },
    story: {
      title: 'Tarinamme',
      text1: 'Olemme suosikki paikallinen ravintolasi Porvoossa, kutsumme sinut kokemaan herkullisen juhlan ystävien ja perheen kanssa.',
      text2: 'Tuo laheisesi nauttimaan herkullisesta pizzasta ja donerista! Pizza Sofra on paikka kaikille. Tarjoamme parhaan ruoan: voimakkaat maut, tuore tuotteet, rapea kana, gourmet-hampurilaiset, tuoreet salaatit ja ainutlaatuisen tunnelman.',
      closing: 'Hyvää ruokahalua!'
    },
    location: {
      title: 'Sijaintimme',
      subtitle: 'Vieraile luonamme ja koe poikkeuksellista ruokaa',
      restaurantName: 'Pizza Sofra',
      orderButton: 'Tilaa Verkossa'
    },
    footer: {
      rights: '2025 Pizza Sofra. Kaikki oikeudet pidet��an.'
    }
  },
  en: {
    nav: {
      home: 'Home',
      story: 'Our Story',
      location: 'Location',
      orderOnline: 'Order Online'
    },
    hero: {
      welcome: 'Welcome to',
      title: 'Pizza Sofra',
      subtitle: '',
      orderButton: 'View Menu & Order'
    },
    story: {
      title: 'Our Story',
      text1: 'We are your favourite local restaurant in Porvoo, inviting you to come together with your friends and family to share a delicious feast.',
      text2: 'Bring your loved ones to experience delicious pizza and doner! Pizza Sofra is a place for everyone. We bring to you the very best cooking: robust flavours, beautifully fresh produce, crispy chicken, gourmet hamburgers, fresh salads, and an atmosphere you cannot find anywhere else.',
      closing: 'Afiyet olsun!'
    },
    location: {
      title: 'Our Location',
      subtitle: 'Visit us and experience exceptional food',
      restaurantName: 'Pizza Sofra',
      orderButton: 'Order Online'
    },
    footer: {
      rights: '© 2025 Pizza Sofra. All rights reserved.'
    }
  }
};

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState('fi'); // Finnish as default
  
  const t = translations[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-amber-900 font-serif">Pizza Sofra</h1>
            </div>
            
            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#home" className="text-gray-700 hover:text-amber-600 transition font-medium">{t.nav.home}</a>
              <a href="#story" className="text-gray-700 hover:text-amber-600 transition font-medium">{t.nav.story}</a>
              <a href="#location" className="text-gray-700 hover:text-amber-600 transition font-medium">{t.nav.location}</a>
              
              {/* Language Selector */}
              <div className="flex items-center gap-2 border-l pl-4">
                <Globe className="w-4 h-4 text-gray-600" />
                <button
                  onClick={() => setLanguage('fi')}
                  className={`px-3 py-1 rounded transition ${
                    language === 'fi' 
                      ? 'bg-amber-600 text-white font-bold' 
                      : 'text-gray-600 hover:text-amber-600'
                  }`}
                >
                  FI
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1 rounded transition ${
                    language === 'en' 
                      ? 'bg-amber-600 text-white font-bold' 
                      : 'text-gray-600 hover:text-amber-600'
                  }`}
                >
                  EN
                </button>
              </div>
              
              <Button
                data-testid="header-order-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => window.open('https://www.restaurantlogin.com/api/fb/0yv_e_y', '_blank')}
              >
                {t.nav.orderOnline}
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-amber-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col gap-4">
              <a href="#home" className="text-gray-700 hover:text-amber-600 transition font-medium" onClick={() => setMobileMenuOpen(false)}>{t.nav.home}</a>
              <a href="#story" className="text-gray-700 hover:text-amber-600 transition font-medium" onClick={() => setMobileMenuOpen(false)}>{t.nav.story}</a>
              <a href="#location" className="text-gray-700 hover:text-amber-600 transition font-medium" onClick={() => setMobileMenuOpen(false)}>{t.nav.location}</a>
              
              {/* Mobile Language Selector */}
              <div className="flex items-center gap-2 py-2 border-t">
                <Globe className="w-4 h-4 text-gray-600" />
                <button
                  onClick={() => setLanguage('fi')}
                  className={`px-3 py-1 rounded transition ${
                    language === 'fi' 
                      ? 'bg-amber-600 text-white font-bold' 
                      : 'text-gray-600 hover:text-amber-600'
                  }`}
                >
                  FI
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1 rounded transition ${
                    language === 'en' 
                      ? 'bg-amber-600 text-white font-bold' 
                      : 'text-gray-600 hover:text-amber-600'
                  }`}
                >
                  EN
                </button>
              </div>
              
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white w-full"
                onClick={() => {
                  window.open('https://www.restaurantlogin.com/api/fb/0yv_e_y', '_blank');
                  setMobileMenuOpen(false);
                }}
              >
                {t.nav.orderOnline}
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1579751626657-72bc17010498?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxuZWFwb2xpdGFuJTIwcGl6emElMjB3b29kJTIwZmlyZWR8ZW58MHx8fHwxNzY0MjAwMjg0fDA&ixlib=rb-4.1.0&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <p className="text-amber-400 text-xl mb-4 font-serif">{t.hero.welcome}</p>
          <h2 className="text-7xl md:text-8xl font-bold mb-12 font-serif">{t.hero.title}</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="order-now-btn"
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg"
              onClick={() => window.open('https://www.restaurantlogin.com/api/fb/0yv_e_y', '_blank')}
            >
              {t.hero.orderButton}
            </Button>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section id="story" className="py-20 px-4 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-5xl font-bold mb-6 text-amber-900 font-serif">{t.story.title}</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {t.story.text1}
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {t.story.text2}
            </p>
            <p className="text-2xl font-serif text-amber-700 italic">{t.story.closing}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img
              src="https://images.unsplash.com/photo-1740479783022-1006a2138849?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxwaXp6YSUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NjQyMDAxNjd8MA&ixlib=rb-4.1.0&q=85"
              alt="Restaurant Interior"
              className="rounded-lg shadow-lg h-64 w-full object-cover"
            />
            <img
              src="https://images.unsplash.com/photo-1672856399643-47ddf6b2d6d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHx0dXJraXNoJTIwcGl6emElMjBkJUMzJUI2bmVyfGVufDB8fHx8MTc2NDIwMDE0Nnww&ixlib=rb-4.1.0&q=85"
              alt="Pizza Making"
              className="rounded-lg shadow-lg h-64 w-full object-cover mt-8"
            />
            <img
              src="https://images.unsplash.com/photo-1685397067025-d293a18099ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxwaXp6YSUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NjQyMDAxNjd8MA&ixlib=rb-4.1.0&q=85"
              alt="Dining Area"
              className="rounded-lg shadow-lg h-64 w-full object-cover mt-8"
            />
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-12 text-amber-900 font-serif">{t.location.title}</h2>
          <p className="text-center text-gray-600 mb-12 text-xl">{t.location.subtitle}</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="overflow-hidden shadow-xl">
              <div className="h-64 bg-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1968.1234567890!2d25.6645!3d60.3944!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNjDCsDIzJzM5LjgiTiAyNcKwMzknNTIuMiJF!5e0!3m2!1sen!2sfi!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Pizza Sofra Location"
                ></iframe>
              </div>
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold mb-4 text-amber-900">{t.location.restaurantName}</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                    <p className="text-gray-700">Pellavapolku 1, 06450 Porvoo</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <a href="tel:0468113133" className="text-gray-700 hover:text-amber-600">046 811 31 33</a>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                    <p className="text-gray-700">MA-SU: 10:30-22:00</p>
                  </div>
                </div>
                <Button
                  className="w-full mt-6 bg-amber-600 hover:bg-amber-700"
                  onClick={() => window.open('https://www.restaurantlogin.com/api/fb/0yv_e_y', '_blank')}
                >
                  {t.location.orderButton}
                </Button>
              </CardContent>
            </Card>

            <img
              src="https://images.pexels.com/photos/5953515/pexels-photo-5953515.jpeg"
              alt="Pizza Sofra Porvoo"
              className="rounded-xl shadow-xl h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-4 font-serif">Pizza Sofra</h3>
          <p className="mb-2">Pellavapolku 1, 06450 Porvoo</p>
          <p className="mb-2">Tel: 046 811 31 33</p>
          <p className="mb-6">MA-SU: 10:30-22:00</p>
          <div className="border-t border-amber-700 pt-6 mt-6">
            <p className="text-amber-200">{t.footer.rights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;